import { Injectable, computed, inject } from '@angular/core';
import { UserService } from '../user/user.service';

/** Minimal shape needed to decide whether a template row is manageable by this session. */
export interface ScopedTemplate {
  customerId?: string | null;
}

/**
 * Owns everything that differs between a normal customer session and a Transflo super-admin
 * working on global templates.
 *
 * Components ask what is *allowed*, never *who* the user is - so when the rules change (for
 * example "some admins may publish"), this file changes and its consumers do not. UserService
 * stays the only place that inspects identity.
 */
@Injectable({
  providedIn: 'root',
})
export class StudioCapabilitiesService {
  private readonly user = inject(UserService);

  /** A global admin authors templates, never runnable workflows. */
  public canPublishWorkflow = computed(() => !this.user.isGlobalAdmin());

  /** Global templates carry no division, so there is no fleet to pick. */
  public canSelectFleet = computed(() => !this.user.isGlobalAdmin());

  /**
   * Global templates deliberately carry no reference JSON, so JSON-derived configuration
   * (JSON pre-populate, dynamic naming, Load Details) is not offered.
   */
  public canUseReferenceJson = computed(() => !this.user.isGlobalAdmin());

  /** A global template is never executed, so there is nothing to trigger on. */
  public canConfigureTrigger = computed(() => !this.user.isGlobalAdmin());

  /** Workflow setup opens on Fleets for customers, which admins do not have. */
  public setupEntryRoute = computed(() =>
    this.user.isGlobalAdmin() ? 'description' : 'fleets'
  );

  /** Every template an admin saves is global, so the menu says so. */
  public templateSaveLabel = computed(() =>
    this.user.isGlobalAdmin() ? 'Save as Global Template' : 'Save as Template'
  );

  /** True when a template this session saves will be global. Drives the menu wording. */
  public savesGlobalTemplates = computed(() => this.user.isGlobalAdmin());

  /**
   * Menu wording for saving one named entity - a segment, step or action - as a template.
   * The save is already scoped by the server from the session; this only makes the menu say
   * which kind the user is about to create.
   */
  public templateSaveLabelFor = (name: string): string =>
    this.savesGlobalTemplates()
      ? `Save ${name} as Global Template`
      : `Save ${name} as Template`;

  /**
   * Admins see only global templates, so any list scoped to templates the session owns is always
   * empty for them - the dashboard's "Own Templates", the step tab's "Created By you" and the
   * action tab's "Your Action Templates".
   */
  public showOwnTemplatesList = computed(() => !this.user.isGlobalAdmin());

  /** A global template is never published, so its status is always Inactive and carries no signal. */
  public showsWorkflowStatus = computed(() => !this.user.isGlobalAdmin());

  /** Global templates are never attached to a load, so Last Used is always blank. */
  public showsLastUsed = computed(() => !this.user.isGlobalAdmin());

  /** Priority orders workflows within a division; a global template has no division. */
  public showsWorkflowPriority = computed(() => !this.user.isGlobalAdmin());

  /** A global workflow carries no division, so the enabled-fleet cell is always empty. */
  public showsEnabledFleet = computed(() => !this.user.isGlobalAdmin());

  /**
   * Whether this session may edit, delete or export a given template.
   *
   * A customer manages only templates they own; a global template (no customerId) is read-only to
   * them. For an admin the reverse holds - every template they can see is global and theirs.
   */
  public canManageTemplate = (
    template: ScopedTemplate | null | undefined
  ): boolean => {
    if (!template) return false;
    return this.user.isGlobalAdmin()
      ? !template.customerId
      : !!template.customerId;
  };
}
